import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";

import { Badge } from "@/components/ui/badge";
import {
  MessageSquare,
  Send,
  Loader2,
  Bell,
  Trash2,
  Plus,
  Search,
  RefreshCw,
  Reply,
  X,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import AdminChatPanel from "@/components/admin/AdminChatPanel";
import { sanitizeMessage } from "@/lib/sanitizeText";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface QA {
  id: string;
  lesson_id: string;
  user_id: string;
  author_name: string;
  content: string;
  is_admin_reply: boolean;
  parent_id: string | null;
  created_at: string;
  lesson_title?: string;
}

interface AdminQATabProps {
  sessionToken: string;
  highlightQuestionId?: string | null;
  onHighlightHandled?: () => void;
}

const QA_HIGHLIGHT_KEY = "admin_qa_highlight_id";

const AdminQATab = ({ sessionToken, highlightQuestionId, onHighlightHandled }: AdminQATabProps) => {
  const [items, setItems] = useState<QA[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | "unanswered">("unanswered");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [replyMap, setReplyMap] = useState<Record<string, string>>({});
  const PAGE_SIZE = 25;
  const [openReplyId, setOpenReplyId] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [sending, setSending] = useState<string | null>(null);
  const [pendingHighlight, setPendingHighlight] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    return sessionStorage.getItem(QA_HIGHLIGHT_KEY);
  });
  const [flashId, setFlashId] = useState<string | null>(null);
  const rowRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const { toast } = useToast();

  const toggleExpanded = (id: string) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const load = async () => {
    setLoading(true);
    const from = page * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;

    // Step 1: fetch top-level questions for the current page (server-side filter + search + count)
    let qBuilder = supabase
      .from("lesson_qa")
      .select("*", { count: "exact" })
      .is("parent_id", null)
      .eq("is_admin_reply", false)
      .order("created_at", { ascending: false });

    const term = debouncedSearch.trim();
    if (term) {
      // Escape % and _ to avoid wildcard injection
      const safe = term.replace(/[\\%_]/g, (m) => `\\${m}`);
      qBuilder = qBuilder.or(`content.ilike.%${safe}%,author_name.ilike.%${safe}%`);
    }

    const { data: questions, count } = await qBuilder.range(from, to);
    setTotalCount(count ?? 0);

    if (!questions || questions.length === 0) {
      setItems([]);
      setLoading(false);
      return;
    }

    // Step 2: fetch replies + lesson titles in parallel
    const questionIds = questions.map((q: any) => q.id);
    const lessonIds = [...new Set(questions.map((q: any) => q.lesson_id))];
    const [{ data: replyRows }, { data: lessons }] = await Promise.all([
      supabase.from("lesson_qa").select("*").in("parent_id", questionIds).order("created_at", { ascending: true }),
      supabase.from("lessons").select("id, title").in("id", lessonIds),
    ]);

    const titleMap = new Map((lessons ?? []).map((l: any) => [l.id, l.title]));
    const decorated = [
      ...questions.map((q: any) => ({ ...q, lesson_title: titleMap.get(q.lesson_id) })),
      ...((replyRows ?? []) as any[]).map((r) => ({ ...r, lesson_title: titleMap.get(r.lesson_id) })),
    ];

    // Optional secondary filter: when searching, also include questions whose lesson title matches
    let finalItems = decorated;
    if (term) {
      const lower = term.toLowerCase();
      const matchedQIds = new Set(
        decorated
          .filter((q) => !q.parent_id && (q.lesson_title || "").toLowerCase().includes(lower))
          .map((q) => q.id),
      );
      // Keep all returned items as-is; lesson title match happens via questions already loaded.
      // (No-op placeholder to keep behavior obvious.)
      void matchedQIds;
    }

    setItems(finalItems);
    setLoading(false);
  };

  // Debounce search input (300ms)
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  // Reset to page 0 when filter changes
  useEffect(() => {
    setPage(0);
  }, [filter]);

  useEffect(() => {
    load();
    // Live updates: any new question or reply refreshes the inbox
    const channel = supabase
      .channel("admin-lesson-qa")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "lesson_qa" },
        () => {
          // Only auto-refresh first page to avoid disrupting pagination context
          if (page === 0) load();
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, debouncedSearch]);

  // Capture incoming highlight prop into pending state + sessionStorage (so it survives reload)
  useEffect(() => {
    if (!highlightQuestionId) return;
    setPendingHighlight(highlightQuestionId);
    try { sessionStorage.setItem(QA_HIGHLIGHT_KEY, highlightQuestionId); } catch {}
    onHighlightHandled?.();
  }, [highlightQuestionId, onHighlightHandled]);

  // Once items are loaded, switch filter if needed, scroll to the row, and flash it
  useEffect(() => {
    if (!pendingHighlight || loading) return;
    const target = items.find((q) => q.id === pendingHighlight);
    if (!target) return;
    // Ensure visible: if it's an answered question, switch to "all"
    setFilter("all");
    // Allow render then scroll
    requestAnimationFrame(() => {
      const el = rowRefs.current.get(pendingHighlight);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setFlashId(pendingHighlight);
        setTimeout(() => setFlashId(null), 2400);
      }
      setPendingHighlight(null);
      try { sessionStorage.removeItem(QA_HIGHLIGHT_KEY); } catch {}
    });
  }, [pendingHighlight, loading, items]);

  const replies = items.filter((q) => q.parent_id);
  const repliesByParent = new Map<string, QA[]>();
  for (const r of replies) {
    if (!repliesByParent.has(r.parent_id!)) repliesByParent.set(r.parent_id!, []);
    repliesByParent.get(r.parent_id!)!.push(r);
  }

  const allQuestions = items.filter((q) => !q.parent_id && !q.is_admin_reply);
  // Note: unansweredCount reflects the *current page* only (server-side count is for total questions).
  const unansweredCount = allQuestions.filter((q) => !repliesByParent.has(q.id)).length;

  let questions = allQuestions;
  if (filter === "unanswered") {
    questions = questions.filter((q) => !repliesByParent.has(q.id));
  }
  // Search is now performed server-side in load(); no client-side filter needed.

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const reply = async (questionId: string, lessonId: string) => {
    const text = replyMap[questionId]?.trim();
    if (!text) return;
    setSending(questionId);
    const { data, error } = await supabase.functions.invoke("manage-certificates", {
      body: { action: "reply_lesson_qa", sessionToken, questionId, lessonId, content: text },
      headers: { Authorization: "" },
    });
    if (error || data?.error || !data?.success) {
      toast({
        title: "Failed to reply",
        description: error?.message || data?.error || "Reply failed",
        variant: "destructive",
      });
    } else {
      setReplyMap((prev) => ({ ...prev, [questionId]: "" }));
      toast({ title: "Reply sent" });
      load();
    }
    setSending(null);
  };

  const deleteQuestion = async (questionId: string) => {
    setDeleting(questionId);
    const { data, error } = await supabase.functions.invoke("manage-certificates", {
      body: { action: "delete_lesson_qa", sessionToken, questionId },
      headers: { Authorization: "" },
    });
    if (error || data?.error || !data?.success) {
      toast({
        title: "Failed to delete",
        description: error?.message || data?.error || "Delete failed",
        variant: "destructive",
      });
    } else {
      setItems((prev) => prev.filter((q) => q.id !== questionId && q.parent_id !== questionId));
      toast({ title: "Question deleted", description: "Removed for the student in real time." });
    }
    setDeleting(null);
    setConfirmDeleteId(null);
  };

  return (
    <div className="space-y-4">
      {/* Primary work area: stacked single column. Chat is a fixed-height card with internal scroll. */}
      <div className="space-y-4">
        <section className="min-w-0 h-[340px]">
          <AdminChatPanel sessionToken={sessionToken} />
        </section>

        <section className="min-w-0">
          <Card className="flex flex-col">
            <CardHeader className="pb-3 shrink-0">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <CardTitle className="flex items-center gap-2 text-base">
                  <MessageSquare className="w-5 h-5 text-primary" /> Lesson Q&amp;A Inbox
                  {unansweredCount > 0 && (
                    <Badge variant="destructive" className="h-5 min-w-5 px-1.5 text-[10px]">
                      {unansweredCount}
                    </Badge>
                  )}
                </CardTitle>
                <div className="flex items-center gap-1.5">
                  <Button
                    size="sm"
                    variant={filter === "unanswered" ? "default" : "outline"}
                    onClick={() => setFilter("unanswered")}
                  >
                    Unanswered
                  </Button>
                  <Button
                    size="sm"
                    variant={filter === "all" ? "default" : "outline"}
                    onClick={() => setFilter("all")}
                  >
                    All
                  </Button>
                  <Button size="sm" variant="ghost" onClick={load} title="Refresh">
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="relative mt-3">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  className="pl-9"
                  placeholder="Search by lesson, student, or content…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </CardHeader>
            <CardContent className="pb-4">
              <div className="max-h-[640px] overflow-y-auto pr-1 -mr-1">
                {loading ? (
                  <div className="space-y-3">
                    {[...Array(3)].map((_, i) => (
                      <Skeleton key={i} className="h-32 w-full" />
                    ))}
                  </div>
                ) : questions.length === 0 ? (
                  <div className="text-center py-16">
                    <MessageSquare className="w-10 h-10 mx-auto mb-3 text-muted-foreground/40" />
                    <p className="text-sm text-muted-foreground">
                      {filter === "unanswered"
                        ? "All caught up — no unanswered questions."
                        : "No questions match your search."}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {questions.map((q) => {
                      const qReplies = repliesByParent.get(q.id) ?? [];
                      const answered = qReplies.length > 0;
                      const safe = sanitizeMessage(q.content);
                      const isExpanded = expandedIds.has(q.id);
                      const replyOpen = openReplyId === q.id;
                      return (
                        <div
                          key={q.id}
                          ref={(el) => {
                            if (el) rowRefs.current.set(q.id, el);
                            else rowRefs.current.delete(q.id);
                          }}
                          className={`rounded-lg border bg-card overflow-hidden transition-shadow ${
                            answered ? "border-border" : "border-l-2 border-l-primary"
                          } ${flashId === q.id ? "ring-2 ring-primary shadow-lg" : ""}`}
                        >
                          <div className="px-4 pt-3 pb-2 flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">{q.author_name}</p>
                              <p className="text-xs text-muted-foreground truncate">
                                {q.lesson_title ?? "Lesson"} ·{" "}
                                {new Date(q.created_at).toLocaleString(undefined, {
                                  month: "short",
                                  day: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </p>
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0">
                              {answered ? (
                                <Badge variant="secondary" className="text-[10px]">
                                  {qReplies.length} repl{qReplies.length === 1 ? "y" : "ies"}
                                </Badge>
                              ) : (
                                <Badge className="text-[10px]">New</Badge>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                                title="Delete question (and all replies)"
                                onClick={() => setConfirmDeleteId(q.id)}
                                disabled={deleting === q.id}
                              >
                                {deleting === q.id ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                  <Trash2 className="w-3.5 h-3.5" />
                                )}
                              </Button>
                            </div>
                          </div>

                          <div className="px-4 pb-3">
                            <p
                              className={`text-sm whitespace-pre-wrap break-words [overflow-wrap:anywhere] ${
                                isExpanded ? "" : "line-clamp-6"
                              }`}
                              dir="auto"
                            >
                              {safe.text}
                            </p>
                            {(safe.truncated || safe.text.split("\n").length > 6 || safe.text.length > 320) && (
                              <button
                                type="button"
                                onClick={() => toggleExpanded(q.id)}
                                className="text-[11px] mt-1 underline text-muted-foreground hover:text-foreground"
                              >
                                {isExpanded ? "Show less" : "Show more"}
                              </button>
                            )}
                          </div>

                          {qReplies.length > 0 && (
                            <div className="border-t bg-muted/20 px-4 py-3 space-y-2">
                              {qReplies.map((r) => {
                                const rs = sanitizeMessage(r.content);
                                return (
                                  <div
                                    key={r.id}
                                    className={`p-2.5 rounded-md text-sm ${
                                      r.is_admin_reply
                                        ? "bg-primary/5 border-l-2 border-l-primary"
                                        : "bg-background border"
                                    }`}
                                  >
                                    <div className="text-xs text-muted-foreground mb-1">
                                      <b className="text-foreground">{r.author_name}</b>
                                      {r.is_admin_reply && " 👑"} ·{" "}
                                      {new Date(r.created_at).toLocaleString(undefined, {
                                        month: "short",
                                        day: "numeric",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })}
                                    </div>
                                    <p className="whitespace-pre-wrap break-words [overflow-wrap:anywhere]" dir="auto">
                                      {rs.text}
                                    </p>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {!replyOpen ? (
                            <div className="border-t px-4 py-2 flex justify-end bg-background">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 text-xs"
                                onClick={() => setOpenReplyId(q.id)}
                              >
                                <Reply className="w-3.5 h-3.5 mr-1" /> Reply
                              </Button>
                            </div>
                          ) : (
                            <div className="border-t px-4 py-3 flex gap-2 items-end bg-background">
                              <Textarea
                                autoFocus
                                placeholder="Reply as Admin…"
                                value={replyMap[q.id] ?? ""}
                                onChange={(e) =>
                                  setReplyMap((prev) => ({ ...prev, [q.id]: e.target.value }))
                                }
                                className="min-h-[44px] max-h-32 text-sm resize-none flex-1"
                              />
                              <div className="flex flex-col gap-1">
                                <Button
                                  size="sm"
                                  onClick={() => reply(q.id, q.lesson_id)}
                                  disabled={sending === q.id || !(replyMap[q.id] ?? "").trim()}
                                >
                                  {sending === q.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Send className="w-4 h-4" />
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7"
                                  onClick={() => setOpenReplyId(null)}
                                  title="Cancel"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              {totalCount > PAGE_SIZE && (
                <div className="flex items-center justify-between gap-2 pt-3 mt-3 border-t text-xs text-muted-foreground">
                  <span>
                    Showing {totalCount === 0 ? 0 : page * PAGE_SIZE + 1}–
                    {Math.min((page + 1) * PAGE_SIZE, totalCount)} of {totalCount}
                  </span>
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 px-2"
                      disabled={page === 0 || loading}
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                    >
                      Prev
                    </Button>
                    <span className="px-2">
                      {page + 1} / {totalPages}
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 px-2"
                      disabled={page + 1 >= totalPages || loading}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </section>
      </div>

      {/* Telegram chat routing stays with Q&A; daily-post channel cards moved to the Social tab. */}
      <div className="space-y-4">
        <AdminTelegramChats />
      </div>

      <AlertDialog
        open={!!confirmDeleteId}
        onOpenChange={(open) => !open && setConfirmDeleteId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this question?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the question and all its replies for both you and
              the student. The student will see it disappear in real time. This cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmDeleteId && deleteQuestion(confirmDeleteId)}
              disabled={!!deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

interface AdminChat {
  id: string;
  chat_id: number;
  label: string | null;
}

const AdminTelegramChats = () => {
  const [chats, setChats] = useState<AdminChat[]>([]);
  const [loading, setLoading] = useState(true);
  const [chatId, setChatId] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    const { data } = await supabase.functions.invoke("admin-telegram-chats", { method: "GET" as any });
    setChats((data as any)?.chats ?? []);
    setLoading(false);
  };
  useEffect(() => {
    load();
  }, []);

  const add = async () => {
    const id = chatId.trim();
    if (!id) return;
    setBusy(true);
    const { data, error } = await supabase.functions.invoke("admin-telegram-chats", {
      body: { action: "add", chat_id: id, label: label.trim() },
    });
    if (error || (data as any)?.error) {
      toast({ title: "Failed", description: error?.message || (data as any)?.error, variant: "destructive" });
    } else {
      toast({ title: "Added", description: "Confirmation ping sent to Telegram." });
      setChatId("");
      setLabel("");
      load();
    }
    setBusy(false);
  };

  const remove = async (id: string) => {
    const { error } = await supabase.functions.invoke("admin-telegram-chats", {
      body: { action: "remove", id },
    });
    if (error) toast({ title: "Failed", description: error.message, variant: "destructive" });
    else load();
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Bell className="w-4 h-4 text-primary" /> Telegram Admin Recipients
          {chats.length > 0 && (
            <span className="ml-auto text-xs font-normal text-muted-foreground">
              {chats.length} chat{chats.length === 1 ? "" : "s"}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 pb-3">
        <div className="flex flex-wrap gap-1.5">
          <Input
            className="h-8 text-xs flex-1 min-w-[140px]"
            placeholder="Chat ID"
            value={chatId}
            onChange={(e) => setChatId(e.target.value)}
          />
          <Input
            className="h-8 text-xs flex-1 min-w-[120px]"
            placeholder="Label (optional)"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
          <Button size="sm" className="h-8 px-2.5" onClick={add} disabled={busy || !chatId.trim()}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
          </Button>
        </div>

        {loading ? (
          <Skeleton className="h-8 w-full" />
        ) : chats.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">No admin chats registered yet.</p>
        ) : (
          <div className="max-h-32 overflow-y-auto space-y-1 -mx-1 px-1">
            {chats.map((c) => (
              <div
                key={c.id}
                className="flex items-center justify-between text-xs px-2 py-1 rounded-md bg-muted/40"
              >
                <div className="truncate">
                  <b>{c.chat_id}</b>
                  {c.label && <span className="text-muted-foreground"> — {c.label}</span>}
                </div>
                <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => remove(c.id)}>
                  <Trash2 className="w-3 h-3 text-destructive" />
                </Button>
              </div>
            ))}
          </div>
        )}

        <details className="text-xs text-muted-foreground">
          <summary className="cursor-pointer hover:text-foreground">How to get a chat ID?</summary>
          <p className="mt-1.5 leading-relaxed">
            Message <b>@userinfobot</b> on Telegram, copy your numeric ID, and paste it above.
          </p>
        </details>
      </CardContent>
    </Card>
  );
};

export default AdminQATab;
